import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { atom, useAtom, useSetAtom } from 'jotai';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/20/solid';

import { ButtonV2, Title, TextInput, Combobox, Alert } from '@restworld/ui-ds';
import { useAuth } from '@restworld/feature-authentication';
import { JobApplication, Variable } from '@restworld/utility-types';
import { useVariableOptions } from '@restworld/utils-common';

import useUpdateJobApplication from '../../../../hooks/use-update-job-application';
import { candidateModalVariantAtom, isCandidateModalSuccessScreenAtom } from '../../../../employer-job-listing.atoms';
import { useEmployerJobApplicationQueryKey } from '../../../../hooks/use-employer-job-applications';

export enum DiscardCandidateSteps {
  INPUT_REASON,
  VIEW_SUCCESS_MESSAGE,
}

function DiscardCandidateReason({
  firstName,
  jobPositionId,
  workerId,
  setStep,
  step,
}: {
  firstName: string;
  workerId: string;
  jobPositionId: string;
  setStep: Dispatch<SetStateAction<DiscardCandidateSteps>>;
  step: DiscardCandidateSteps;
}) {
  const { t } = useTranslation('job-positions-page');
  const { control, handleSubmit, watch } = useForm<DiscardFormValues>();
  const { user } = useAuth();
  const { mutate, isSuccess, isPending } = useUpdateJobApplication();
  const [workerDiscardedReasonVariable] = useAtom(workerDiscardedReasonVariableAtom);
  const setCandidateModalVariant = useSetAtom(candidateModalVariantAtom);
  const [, setIsCandidateModalSuccessScreen] = useAtom(isCandidateModalSuccessScreenAtom);
  const queryClient = useQueryClient();
  const employerJobApplicationQueryKey = useEmployerJobApplicationQueryKey();

  useWorkerDiscardedReasonVariable();
  const canSubmit = !!watch('reason');
  const onSubmit: SubmitHandler<DiscardFormValues> = useCallback(
    (values) => {
      const reasonId = workerDiscardedReasonVariable?.find((v) => v.label === values.reason)?.id;
      mutate({
        params: {
          discarded_reason: reasonId,
          discarded_other_reason: values.otherDetails,
          discarded_by: user?.type as JobApplication['discarded_by'],
          status_key: 'worker_jp_discarded',
        },
        workerId,
        jobPositionId,
      });
    },
    [jobPositionId, mutate, user?.type, workerDiscardedReasonVariable, workerId]
  );

  const handleCancel = useCallback(() => {
    setCandidateModalVariant('default');
  }, [setCandidateModalVariant]);

  useEffect(() => {
    if (!isSuccess) return;
    queryClient.invalidateQueries({ queryKey: employerJobApplicationQueryKey });
    setIsCandidateModalSuccessScreen(true);
    setStep(DiscardCandidateSteps.VIEW_SUCCESS_MESSAGE);
  }, [employerJobApplicationQueryKey, isSuccess, queryClient, setIsCandidateModalSuccessScreen, setStep]);

  return (
    <StyledForm>
      <ContentAndInputsWrapper>
        <NoSymbolIcon tw="h-6 w-6 text-red-700 inline-block mx-auto mb-5" />
        <Title fontSize="large" textColor="neutral-700" fontWeight="semibold" twStyle={tw`w-full text-base lg:text-lg`}>
          {`${t('you_are_discarding_name', { name: firstName })}`}
        </Title>
        <ContentWrapper>{t('tell_us_what_did_not_convince_you_it_will_help_us_improve_our_research')}</ContentWrapper>
        <InputsWrapper>
          <InputWrapper>
            <StyledLabel htmlFor="reason">{t('reason')}</StyledLabel>
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <Combobox
                  dropdownList={workerDiscardedReasonVariable?.map((v) => v.label) || []}
                  placeholder={t('select_the_reason')}
                  inputWrapperStyle={tw`w-full`}
                  fullwidth
                  {...field}
                />
              )}
            />
          </InputWrapper>
          <InputWrapper>
            <div tw="flex justify-between">
              <StyledLabel htmlFor="otherDetails">{t('other_details')}</StyledLabel>
              <span tw="text-neutral-100 text-sm">{`(${t('optional')})`}</span>
            </div>
            <Controller
              name="otherDetails"
              control={control}
              render={({ field }) => (
                <TextInput id="other-details-input" type="text" bordercolor="neutral-100" {...field} />
              )}
            />
          </InputWrapper>
        </InputsWrapper>
        {canSubmit && (
          <div tw="mt-10">
            <Alert
              icon={<InformationCircleIcon className="text-red-700 h-4 w-4 min-w-[16px] min-h-[16px]" />}
              text={t('if_you_proceed_we_will_communicate_to_the_candidate_your_choice')}
              variant="error"
              textStyling={tw`font-medium text-red-700`}
              wrapperStyling={tw`border-red-700`}
            />
          </div>
        )}
      </ContentAndInputsWrapper>
      <ButtonsWrapper>
        <ButtonV2 type="button" variant="tertiary" onClick={handleCancel}>
          {t('cancel')}
        </ButtonV2>
        <ButtonV2
          type="submit"
          disabled={!canSubmit}
          loading={isPending}
          variant="secondary-danger"
          onClick={handleSubmit(onSubmit)}
        >
          {t('discard_and_send_feedback')}
        </ButtonV2>
      </ButtonsWrapper>
    </StyledForm>
  );
}
export default DiscardCandidateReason;

const StyledForm = styled.form`
  ${tw`h-full flex flex-col justify-between`}
`;
const ContentWrapper = styled.p`
  ${tw`text-neutral-200 mt-5 mb-12 font-medium`}
`;
const ContentAndInputsWrapper = styled.div`
  ${tw``}
`;
const InputsWrapper = styled.div`
  ${tw`flex flex-col gap-6 max-w-[320px] mx-auto lg:(max-w-[400px])`}
`;
const InputWrapper = styled.div<{ isHidden?: boolean }>`
  ${({ isHidden }) => (isHidden ? tw`hidden` : tw``)}
`;
const StyledLabel = styled.label`
  ${tw`text-start font-medium text-neutral-700 text-base flex`}
`;
const ButtonsWrapper = styled.div`
  ${tw`mt-8 flex justify-between items-center p-4 gap-4 border-neutral-40 lg:(px-20 border-t absolute bottom-0 left-0 right-0) 2xl:(-left-10 -right-10)`}
`;

interface DiscardFormValues {
  reason?: string;
  otherDetails?: string;
}

const workerDiscardedReasonVariableAtom = atom<Variable[]>([]);

const useWorkerDiscardedReasonVariable = () => {
  const [workerDiscardedReasonVariable, setWorkerDiscardedReasonVariable] = useAtom(workerDiscardedReasonVariableAtom);

  const { fetchVariable, variableOptions } = useVariableOptions();

  useEffect(() => {
    if (!workerDiscardedReasonVariable?.length) fetchVariable(['worker_discarded_reason']);
  }, [workerDiscardedReasonVariable?.length, fetchVariable]);

  useEffect(() => {
    if (variableOptions?.['worker_discarded_reason']) {
      const workerDiscardedReasonVariableByEmployer = variableOptions['worker_discarded_reason']
        .filter((v) => v.parent_key === 'employer')
        .map((v) => {
          if (v.key === 'already_knows_worker') return { ...v, label: 'Conosco già il worker' };
          return v;
        });

      setWorkerDiscardedReasonVariable(workerDiscardedReasonVariableByEmployer);
    }
  }, [variableOptions, setWorkerDiscardedReasonVariable]);
};
